import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import {
	PAX_TYPE_ADULT_CODE,
	PAX_TYPE_CHILD_CODE,
	PAX_TYPE_INFANT_CODE,
	PAX_TYPE_YOUNG_ADULT_CODE,
	TRANSLATION_ANCILLARY_DETAILED,
	TRIP_TYPE_HAS_STOP_OVER_CODE,
	TRIP_TYPE_HAS_STOP_OVER_DESCRIPTION,
	TRIP_TYPE_MULTICITY_CODE,
	TRIP_TYPE_MULTICITY_DESCRIPTION,
	TRIP_TYPE_ONE_WAY_CODE,
	TRIP_TYPE_ONE_WAY_DESCRIPTION,
	TRIP_TYPE_ROUND_TRIP_CODE,
	TRIP_TYPE_ROUND_TRIP_DESCRIPTION,
	SSR_CODE_FIREARMS,
	SSR_CODE_SPORTS_SPORTING_EQUIPMENT,
	SSR_CODE_MEAL_CHILD,
	EXTRAS_ANCILLARY_MEAL,
	DISCOUNT_STATUS_ACTIVE
} from '@ibe.config';
import { EventDataLayerActionFieldObject, EventDataLayerObject, EventDataLayerProductObject, EventPaymentCashEnumDimension2, StaticDataLayerObject } from '@ibe.models/google-analytics.indra.model';
import { combineLatest, concat, forkJoin, Observable, of, Subscription } from 'rxjs';
import { INDRABFMDataStorageService } from './bfm-data-storage.indra.service';
import { INDRAStorageService } from './storage.indra.service';
import { INDRABookingService } from './booking.indra.service';
import { INDRADateService } from './date.indra.service';
import { INDRAIbeAdminService } from './ibeadmin.indra.service';
import * as _ from 'lodash';
import { INDRAAddOnAncillary } from '@ibe.models/ancillary.indra.model.lib';
import { Brand } from '@ibe.components/booking/flights/brand/brand.indra';
import * as moment from 'moment';
import { filter, map, take, takeWhile } from 'rxjs/operators';
import { INDRALanguageMarketService } from './language-market-indra.service';
import { FlightEquipmentEnum } from '@ibe.components/shared/_models/enums/flight-equipment.enum';
import { PaymentMethod } from '@ibe.models/payment.model.lib';
import { INDRABookedTripDetails } from '@ibe.models/booked-trip-details.indra.model.lib';
import * as fromStore from '../store';
import { Store } from '@ngrx/store';
import { RouterStateUrl } from '@ibe/store/states/router.state';
import { BookedTrip } from '@ibe/models';
import { environment } from '@environments/environment';
import { INDRAObservatoryService } from '@ibe.services/observatory.indra.service';

export const GTM_SITE = 'IBE';
export const EVENT_LABEL_EC = 'ecommerce';
export const EVENT_ACTION_IMPRESSION = 'Product impression';
export const EVENT_ACTION_CLICK = 'Product click';
export const EVENT_ACTION_ADD_CART = 'Add to cart';
export const EVENT_ACTION_REM_CART = 'Remove from cart';
export const EVENT_ACTION_CHECKOUT = 'Checkout';
export const EVENT_ACTION_PURCHASE = 'Purchase';
export const EVENT_TRIGGER_ADD = 'eecibe.add';
export const EVENT_TRIGGER_CHECKOUT = 'eecibe.checkout';
export const EVENT_TRIGGER_DETAIL = 'eecibe.detail';
export const EVENT_TRIGGER_PURCHASE = 'eecibe.purchase';
export const EVENT_TRIGGER_REMOVE = 'eecibe.remove';

export const EVENT_CATEGORY_ANCILLARIES = 'Ancillaries';
export const EVENT_CATEGORY_EXTRAS = 'Extras';
export const EVENT_CATEGORY_FLIGHTS = 'Flights';
export const EVENT_PRODUCT_BRAND_TAP = 'TAP';
export const EVENT_PRODUCT_BRAND_DANISH_FUND = 'Danish Fund';
export const EVENT_PRODUCT_BRAND_CHUBB_INSURANCE = 'Chubb Insurance';
export const EVENT_PRODUCT_BRAND_CARBON_OFFSET = 'Carbon Offset';
export const EVENT_PRODUCT_BRAND_CARS = 'Cars';
export const EVENT_PRODUCT_BRAND_HOTELS = 'Hotels';
export const EVENT_REVIEW_YOUR_TRIP_STEP_NO = '1';
export const EVENT_EXTRAS_TO_YOUR_TRIP_STEP_NO = '2';
export const EVENT_TIME_TO_THINK_STEP_NO = '1';
export const EVENT_BOOKING_CREATION_STEP_NO = '3';
export const KEY_GA_EECIBE = 'GA_EECIBE';
import {Title} from "@angular/platform-browser";
import { isArray } from 'util';


// Declare GTM dataLayer array.
declare global {
	interface Window {
		dataLayer: any[];
	}
}

// notes: npm install --save-dev @types/google.analytics
@Injectable({ providedIn: 'root' })
export class INDRAGoogleAnalyticsService {
	private subscription: Subscription;
	private staticDataLayerObject: StaticDataLayerObject;
	private gtmId = environment.gtmId;
	private currentUrl: string;
	private previousUrl: string;
	private dataLayerObjects: any;

	bookingSteps$: Observable<any> = this.store.select(fromStore.getSelectedBookingSteps);
	bookingSteps: Array<any> = [];
	bookedTrip$: Observable<BookedTrip> = this.store.select(fromStore.getSelectedBookedTrip);
	bookedTrip: BookedTrip;
	promocoCodesAndVoucher$: Observable<Array<any>> = this.store.select(fromStore.getSelectedPromoVouchers);
	promocoCodesAndVoucher: Array<any> = [];
	bookAwardFlight$: Observable<boolean> = this.store.select(fromStore.getFlightsFilterBookAwardFlights);
	bookAwardFlight: boolean;
	bookStarAllianceFlight$: Observable<boolean> = this.store.select(fromStore.getFlightsFilterStarAllianceFlights);
	bookStarAllianceFlight: boolean;
	getDeeplinkParams$: Observable<boolean> = this.store.select(fromStore.getMiniIBEDeeplinkParams);
	deeplinkParams: any;

  onLoadPageView = true;
  pageViewWithBooking = false;
  lastPageUrl = '';


	constructor(
		private router: Router,
		private dataStorage: INDRABFMDataStorageService,
		private storageService: INDRAStorageService,
		private bookingService: INDRABookingService,
		private dateService: INDRADateService,
		private ibeAdminService: INDRAIbeAdminService,
		private languageMarketService: INDRALanguageMarketService,
		private observatory: INDRAObservatoryService,
		private store: Store<fromStore.State>,
		private titleService:Title
	) {
		window.dataLayer = window.dataLayer || [];
		this.staticDataLayerObject = {} as StaticDataLayerObject;
	}

	public subscribe() {
		if (!this.subscription) {
			// subscribe to any router navigation and once it ends, write out the google script notices
			/*this.subscription = this.router.events.subscribe(e => {
				if (e instanceof NavigationEnd) {
					this.INDRA_setGAStaticDataLayer();
				}
			});*/
			this.subscription = combineLatest(this.store.select(fromStore.getRouterState), this.dataStorage.INDRA_getObservableCurrentBooking())
				.pipe(filter(state => !_.isNil(state[0])))
				.subscribe(([routerState, bookedTrip]) => {
					const routesWithBooking = ['/booking/review-your-trip'];

					if (routerState.url.includes('/my-bookings') && this.storageService.get('finishedPNRGenerate')) {
							this.INDRA_setDataLayerAndPageView(routerState, true);
							this.storageService.remove('finishedPNRGenerate');
					} else if (routerState.url && !routerState.url.includes('/my-bookings')) {
						const currentUrl = routerState.url.split('?');
						
						if (currentUrl[0] && (currentUrl[0] !== '/booking' || _.isEmpty(routerState.queryParams))) {
							const isRouteWithBooking = routesWithBooking.includes(routerState.url);

							if (isRouteWithBooking) {
								if (this.currentUrl !== routerState.url && bookedTrip) {
									this.pageViewWithBooking = true;
									this.INDRA_setDataLayerAndPageView(routerState, true);
								}
							} else {
								if (this.currentUrl !== routerState.url) {
									this.INDRA_setDataLayerAndPageView(routerState, true);
								}
							}
						}

						// call pageView event once per route
						// if (this.currentUrl !== routerState.url) {
						// 	this.INDRA_setDataLayerAndPageView(routerState, true);
						// } else {
						// 	this.INDRA_setDataLayerAndPageView(routerState, false);
						// }
					}
				});

			this.subscription.add(this.promocoCodesAndVoucher$.subscribe(promoCode => {
				this.promocoCodesAndVoucher = Object.assign([], promoCode);
			}));


			this.subscription.add(this.bookingSteps$.subscribe(bookingSteps => {
				this.bookingSteps = bookingSteps;
			}));


			this.subscription.add(this.bookedTrip$.subscribe(bookedTrip => {
				this.bookedTrip = bookedTrip;
			}));

			this.subscription.add(this.bookAwardFlight$.subscribe(bookAwardFlight => {
				this.bookAwardFlight = bookAwardFlight;
			}));

			this.subscription.add(this.bookStarAllianceFlight$.subscribe(bookStarAllianceFlight => {
				this.bookStarAllianceFlight = bookStarAllianceFlight;
			}));

			this.subscription.add(this.getDeeplinkParams$.subscribe(deeplinkParams => {
				this.deeplinkParams = deeplinkParams;
			}));

			// this.subscription.add(
			// 	this.router.events.pipe(filter((val: any) => val instanceof NavigationEnd))
			// 		.subscribe(event => {						
			// 			if (!event.urlAfterRedirects.includes('sidebar:') && !this.previousUrl.includes('sidebar:') && !event.urlAfterRedirects.includes('/booking/payment')
			// 				&& !event.urlAfterRedirects.includes('my-bookings/view')) {
			// 				/** 
			// 				 * Pageview event should only be called after a datalayer gtm
			// 				 * and once per page only
			// 				 * Open and close of sidebar should not be a pageview event
			// 				*/
			// 				this.INDRA_setGAPageView();
			// 			} else if (event.urlAfterRedirects.includes('/booking/payment') && event.urlAfterRedirects.includes('sidebar:')) {
			// 				if (event.urlAfterRedirects.includes('new-passenger')) {
			// 					this.IBE_GA_setAdditionalStepsPaymentPage('passenger');
			// 				} else if (event.urlAfterRedirects.includes('contacts')) {
			// 					this.IBE_GA_setAdditionalStepsPaymentPage('contacts');
			// 				}
			// 			}
			// 		})
			// )
		}
	}

	public unsubscribe() {
		if (this.subscription) {
			// clear our observable subscription
			this.subscription.unsubscribe();
		}
	}

	private setRoutes(routerState: RouterStateUrl) {
		const { url } = routerState;
		if (_.isNil(this.previousUrl)) {
			this.previousUrl = url;
		} else {
			this.previousUrl = this.currentUrl;
		}
		this.currentUrl = url;
	}

	// create gtm
	public addGtmToDom() {
		window.dataLayer.push({
			'gtm.start': new Date().getTime(),
			event: 'gtm.js'
		});

		const gtmScript = document.createElement('script');
		gtmScript.id = 'GTMscript';
		gtmScript.async = true;
		gtmScript.src = '//www.googletagmanager.com/gtm.js?id=' + this.gtmId;
		document.head.insertBefore(gtmScript, document.head.firstChild);

		const ifrm = document.createElement('iframe');
		ifrm.setAttribute('src', '//www.googletagmanager.com/ns.html?id=' + this.gtmId);
		ifrm.style.width = '0';
		ifrm.style.height = '0';
		ifrm.style.display = 'none';
		ifrm.style.visibility = 'hidden';

		const noscript = document.createElement('noscript');
		noscript.id = 'GTMiframe';
		noscript.appendChild(ifrm);

		document.body.insertBefore(noscript, document.body.firstChild);
	}

	INDRA_setGAStaticDataLayer(setGAPageView) {
		this.IBE_getStaticDataLayerObject()
			.pipe(take(1))
			.subscribe(() => {
				window.dataLayer.push(this.staticDataLayerObject);
				if (window.dataLayer.filter(value => _.has(value, 'site')).length === 1) {
					this.addGtmToDom();
				}

				if (setGAPageView) {
					if (!this.currentUrl.includes('sidebar:') && !this.previousUrl.includes('sidebar:') && !this.currentUrl.includes('/booking/payment')
						) {
						/** 
						 * Pageview event should only be called after a datalayer gtm
						 * and once per page only
						 * Open and close of sidebar should not be a pageview event
						*/
						this.INDRA_setGAPageView();
					} else if (this.currentUrl.includes('/booking/payment') && this.currentUrl.includes('sidebar:')) {
						if (this.currentUrl.includes('new-passenger')) {
							this.IBE_GA_setAdditionalStepsPaymentPage('passenger');
						} else if (this.currentUrl.includes('contacts')) {
							this.IBE_GA_setAdditionalStepsPaymentPage('contacts');
						}
					}
				}

			});
	}

	// -------------------------------- START IBE METHODS ---------------------------------- //

	// --------------- START STATIC DATALAYER PROCESSING -----------------//

	private IBE_getStaticDataLayerObject(): Observable<StaticDataLayerObject> {
		this.IBE_resetActiveBookedTrip();

		const market$ = this.languageMarketService.market$;
		const language$ = this.languageMarketService.language$;
		const bfmBookedTrip$ = this.IBE_getBookingObservableByRouter();

		return combineLatest(market$, language$, bfmBookedTrip$, this.bookingSteps$, this.bookedTrip$)
			.pipe(
				filter(observables$ => this.IBE_checkObservablesPerRoute(...observables$)),
				map(source => {
					const [mkt, lang, bfmBookedTrip, bookingSteps, bookedTrip] = source;

					const paths = this.currentUrl.split('/').filter(v => !_.isEmpty(v));
					const first = _.head(paths);
					const tail = _.tail(paths);

					// all pages
					this.IBE_createGenericStaticDataLayer(mkt, lang);

					// dates page -> confirmation page
					if (tail.length > 0 && bookingSteps.length && bookedTrip) {
						this.IBE_createFlightDataLayer(bookingSteps, bookedTrip, bfmBookedTrip);
					}

					return this.staticDataLayerObject;
				})
			);
	}

	private IBE_isBookingPossibleInStorageByRouter(): boolean {
		return this.dataStorage.INDRA_getActiveBookedTrip() ? true : false;
	}

	private IBE_getBookingObservableByRouter(): Observable<INDRABookedTripDetails> {
		if (this.IBE_isBookingPossibleInStorageByRouter()) {
			return this.dataStorage.INDRA_getObservableCurrentBooking();
		}
		return of(null);
	}

	private IBE_checkObservablesPerRoute(...values: any[]): boolean {
		const [mkt, lang, booking] = values;
		const requiredValues = !_.isNil(mkt) && !_.isNil(lang);
		if (this.IBE_isBookingPossibleInStorageByRouter()) {
			return requiredValues && !_.isNil(booking);
		}
		return requiredValues;
	}

	private IBE_resetActiveBookedTrip() {
		const paths = this.currentUrl.split('/').filter(v => !_.isEmpty(v));
		const first = _.head(paths);
		const tail = _.tail(paths);
		const firstCondition = Object.is(first, 'booking') && tail.length === 0;

		if (firstCondition) {
			Object.keys(this.staticDataLayerObject).forEach(key => this.staticDataLayerObject[key] = undefined);
			window.dataLayer.push({
				event: undefined,
				ecommerce: undefined
			});
		}
	}

	private IBE_createGenericStaticDataLayer(mkt, lang) {
		this.staticDataLayerObject.market = mkt;
		this.staticDataLayerObject.language = lang;
		this.staticDataLayerObject.site = GTM_SITE;
		this.staticDataLayerObject.pageName = this.getPageName(this.currentUrl);
		this.staticDataLayerObject.pageUrl = this.currentUrl;
		this.staticDataLayerObject.office_id = undefined; // temporary


		// logged user
		if (this.storageService && this.storageService.get('userData')) {
			const userData = this.storageService.get('userData');
			const userNotLoggedData = this.storageService.get('INDRABFMDataStorageService._activeBooking');
			this.staticDataLayerObject.loggedID = userData.airlineID;
			this.staticDataLayerObject.victoria_user = !_.isEmpty(userData.ffCarrier) && userData.ffCarrier === 'TP' && !_.isEmpty(userData.ffLevel) ? '1' : '0';
			this.staticDataLayerObject.tier_level = !_.isEmpty(userData.ffLevel) ? userData.ffLevel : '';
			this.staticDataLayerObject.miles_balance = userData.currentPoints;
			const consents = userData.consents;
			const forCode = consents ? [
				(consents.tappp ? '1' : '0'),
				(consents.tapsms ? '1' : '0'),
				(consents.tapemail ? '1' : '0'),
				(consents.loytc ? '1' : '0'),
				(consents.loysms ? '1' : '0'),
				(consents.loyemail ? '1' : '0'),
				(consents.tapprof ? '1' : '0'),
				(consents.tappush ? '1' : '0'),
				(consents.loypush ? '1' : '0')
			].join('') : undefined;
			this.staticDataLayerObject.for_code = forCode;
			this.staticDataLayerObject.primary_email = userData.contactEmail;
			if(userNotLoggedData != null){
				this.staticDataLayerObject.customer_email = userNotLoggedData.data.infoPax.email;
			}
		} else {
			const userNotLoggedData = this.storageService.get('INDRABFMDataStorageService._activeBooking');
			this.staticDataLayerObject.loggedID = undefined;
			this.staticDataLayerObject.victoria_user = undefined;
			this.staticDataLayerObject.tier_level = undefined;
			this.staticDataLayerObject.miles_balance = undefined;
			this.staticDataLayerObject.for_code = undefined;
			if(userNotLoggedData != null){
				this.staticDataLayerObject.primary_email = userNotLoggedData.data.infoPax.email;
				this.staticDataLayerObject.customer_email = userNotLoggedData.data.infoPax.email;
			}
		}

		this.staticDataLayerObject.x_tap_source = this.deeplinkParams && this.deeplinkParams.x_tap_source ? this.deeplinkParams.x_tap_source : null;
		this.staticDataLayerObject.x_tap_username = this.deeplinkParams && this.deeplinkParams.x_tap_username ? this.deeplinkParams.x_tap_username : null;
		this.staticDataLayerObject.x_tap_correlationid = this.deeplinkParams && this.deeplinkParams.x_tap_correlationid ? this.deeplinkParams.x_tap_correlationid : null;
	}

	private IBE_createFlightDataLayer(bookingSteps, bookedTrip, bfmBookedTrip) {
		const paths = this.currentUrl.split('/').filter(v => !_.isEmpty(v));
		const first = _.head(paths);

		if (first !== 'my-bookings') {
			const origin = this.IBE_getOriginAirport(bookingSteps);
			const dest = this.IBE_getDestinationAirport(bookingSteps, bookedTrip.booking_type);
			this.staticDataLayerObject.flight_type = this.IBE_getTripTypeName(bookingSteps, bookedTrip.booking_type);
			this.staticDataLayerObject.flight_originairportcity = origin.code;
			this.staticDataLayerObject.flight_destinationairportcity = dest.code;
			this.staticDataLayerObject.flight_origincityname = origin.location.city;
			this.staticDataLayerObject.flight_destinationcityname = dest.location.city;
			this.staticDataLayerObject.flight_route = `${origin.code}-${dest.code}`;
			this.staticDataLayerObject.flight_haul = undefined;
			this.staticDataLayerObject.passengers_nr = bookedTrip.passengerCount;
			this.staticDataLayerObject.adults_nr = bookedTrip.passengers.adults;
			this.staticDataLayerObject.infants_nr = bookedTrip.passengers.infants;
			this.staticDataLayerObject.children_nr = bookedTrip.passengers.children;
			this.staticDataLayerObject.youngadult_nr = bookedTrip.passengers.youngAdults;

			if (this.IBE_allDatesSelected(bookingSteps)) {
				this.staticDataLayerObject.flight_departuredate = moment(bookingSteps[0].date).format('DD.MM.YYYY');
				this.staticDataLayerObject.flight_returndate = moment(bookingSteps[bookingSteps.length - 1].date).format('DD.MM.YYYY');
				this.staticDataLayerObject.award_shopper_availability = this.bookAwardFlight ? 'Yes' : 'No';
				this.staticDataLayerObject.star_alliance_availability = this.bookStarAllianceFlight ? 'Yes' : 'No';
			}
		} else if (first === 'my-bookings' && bfmBookedTrip){
			// uses pnr object instead of store when on booking view / confirmation page
			const originAirportCity = this.getOriginAirportCity(bfmBookedTrip);
			const destinationAirportCity = this.IBE_getDestinationAirportCity(bfmBookedTrip);
			this.staticDataLayerObject.flight_type = this.getTripTypeName(bfmBookedTrip.data.fare.tripType);
			this.staticDataLayerObject.flight_originairportcity = originAirportCity;
			this.staticDataLayerObject.flight_destinationairportcity = destinationAirportCity;
	
			if (bfmBookedTrip.translate && bfmBookedTrip.translate.locations) {
				this.staticDataLayerObject.flight_origincityname = bfmBookedTrip.translate.locations[originAirportCity] ? bfmBookedTrip.translate.locations[originAirportCity].city : '';
				this.staticDataLayerObject.flight_destinationcityname = bfmBookedTrip.translate.locations[destinationAirportCity] ? bfmBookedTrip.translate.locations[destinationAirportCity].city : '';
			}

			this.staticDataLayerObject.flight_route = originAirportCity + '-' + destinationAirportCity;		
			const listPax = bfmBookedTrip.data.infoPax.listPax;	
			this.staticDataLayerObject.passengers_nr = listPax.length.toString();
			this.staticDataLayerObject.adults_nr = this.getPaxNrByType(listPax, PAX_TYPE_ADULT_CODE);
			this.staticDataLayerObject.infants_nr = this.getPaxNrByType(listPax, PAX_TYPE_INFANT_CODE);
			this.staticDataLayerObject.children_nr = this.getPaxNrByType(listPax, PAX_TYPE_CHILD_CODE);
			this.staticDataLayerObject.youngadult_nr = this.getPaxNrByType(listPax, PAX_TYPE_YOUNG_ADULT_CODE);
			this.staticDataLayerObject.flight_departuredate = !_.isEmpty(bfmBookedTrip.data.fare.listOutbound) ? this.dateService.INDRA_formatDate(bfmBookedTrip.data.fare.listOutbound[0].firstSegment.departureDate.toString(), 'DD.MM.YYYY') : '';
			this.staticDataLayerObject.flight_returndate = this.getReturnDate(bfmBookedTrip);
			this.staticDataLayerObject.payment_type = bfmBookedTrip.data.fop ? bfmBookedTrip.data.fop.paymentMethod : '';
			
			const milesUsed = this.storageService.get('shoppingCartMiles');
			this.staticDataLayerObject.miles_used = milesUsed && milesUsed != 0 ? milesUsed.toString() : "0";
			// this.staticDataLayerObject.miles_used_p1 = '';
			// this.staticDataLayerObject.miles_used_p2 = '';

			
		}

		if (bfmBookedTrip) {
			this.staticDataLayerObject.flight_number = this.getFlightNumbers(bfmBookedTrip);
			this.staticDataLayerObject.flight_stopover = this.getStopover(bfmBookedTrip);
			this.staticDataLayerObject.flight_type = this.staticDataLayerObject.flight_stopover === 'yes' ? TRIP_TYPE_HAS_STOP_OVER_DESCRIPTION : this.staticDataLayerObject.flight_type;
			this.staticDataLayerObject.flight_timetothink = bfmBookedTrip.data.isTTT ? 'yes' : 'no';
			this.staticDataLayerObject.travel_duration = this.IBE_getDuration(bfmBookedTrip, this.staticDataLayerObject.flight_type);
			this.staticDataLayerObject.flight_segments = this.getSegments(bfmBookedTrip);
			this.staticDataLayerObject.flight_brand = this.IBE_getAllBrands(bookedTrip.flights);
			this.staticDataLayerObject.flight_rbd = this.getRBDs(bfmBookedTrip);
			this.staticDataLayerObject.flight_cabin = this.getCabins(bfmBookedTrip);
			this.staticDataLayerObject.flight_haul = this.getHauls(bfmBookedTrip);
			this.staticDataLayerObject.flight_bookingdate = bfmBookedTrip.data.creationTime ? this.dateService.INDRA_formatDate(bfmBookedTrip.data.creationTime.toString(), 'DD.MM.YYYY') : null;
			this.staticDataLayerObject.pnr_nr = this.bookingService.getCurrentPNR();
			this.staticDataLayerObject.office_id = bfmBookedTrip.data.office;
		}
	}

	private IBE_getTripTypeName(bookingSteps: Array<any>, bookingType: string) {
		const tripType = bookingSteps.find(step => step.stopover) ? TRIP_TYPE_HAS_STOP_OVER_CODE : bookingType;
		switch (tripType) {
			case TRIP_TYPE_ONE_WAY_CODE:
				return TRIP_TYPE_ONE_WAY_DESCRIPTION;
			case TRIP_TYPE_ROUND_TRIP_CODE:
				return TRIP_TYPE_ROUND_TRIP_DESCRIPTION;
			case TRIP_TYPE_MULTICITY_CODE:
				return TRIP_TYPE_MULTICITY_DESCRIPTION;
			case TRIP_TYPE_HAS_STOP_OVER_CODE:
				return TRIP_TYPE_HAS_STOP_OVER_DESCRIPTION;
			default:
				return '';
		}
	}

	IBE_getOriginAirport(bookingSteps?: any, isEcommerceEvent?: any) {
		if (isEcommerceEvent && this.bookedTrip.flights && this.bookedTrip.flights.length) {
			const originAirportCode = this.bookedTrip.flights[0].listSegment.find(segment => segment.departureAirport).departureAirport;
			return originAirportCode;
		} else {
			let originAirportCity;
			const steps = bookingSteps ? bookingSteps : this.bookingSteps;
			const departure = steps[0].departure === undefined ? steps[0]._departure : steps[0].departure;
			if (departure) {
				originAirportCity = departure;
			}
			return originAirportCity;
		}
	}

	IBE_getDestinationAirport(bookingSteps?: any, bookingType?: string, isEcommerceEvent = false) {
		const steps = bookingSteps ? bookingSteps : this.bookingSteps;
		const type = bookingType ? bookingType : this.bookedTrip.booking_type;
		const hasStopover = steps.find(step => step.stopover) ? TRIP_TYPE_HAS_STOP_OVER_CODE : type;

		if (isEcommerceEvent && this.bookedTrip.flights && this.bookedTrip.flights.length) {
			let destinationAirportCode;
			const flights = this.bookedTrip.flights[this.bookedTrip.flights.length - 1];
			destinationAirportCode = flights.listSegment[flights.listSegment.length - 1].arrivalAirport;

			if (hasStopover && type === TRIP_TYPE_ROUND_TRIP_CODE) {
				const departureAirportCode = flights.listSegment.find(segment => segment.departureAirport).departureAirport;
				destinationAirportCode = departureAirportCode;
			} 
			return destinationAirportCode;

		} else {
			let destinationAirportCity;
			const departure = steps[steps.length - 1].departure === undefined ? steps[steps.length - 1]._departure : steps[steps.length - 1].departure;
			const arrival = steps[steps.length - 1].arrival === undefined ? steps[steps.length - 1]._arrival : steps[steps.length - 1].arrival;
			const arrivalFirst = steps[0].arrival === undefined ? steps[0]._arrival : steps[0].arrival;
	
			switch (type) {
	
				case TRIP_TYPE_ONE_WAY_CODE: destinationAirportCity = hasStopover ? arrival : arrivalFirst; break;
				case TRIP_TYPE_ROUND_TRIP_CODE: destinationAirportCity = hasStopover ? departure : arrivalFirst; break;
				default: destinationAirportCity = steps[steps.length - 1].arrival; break;
			}

			return destinationAirportCity;
		}
	}
	// uses pnr object instead of store
	IBE_getDestinationAirportCity(bfmBookedTrip: any): string {
		let destinationAirportCity = '';
		if (!_.isEmpty(bfmBookedTrip.data.fare.listInbound)) {
			destinationAirportCity = bfmBookedTrip.data.fare.listInbound[bfmBookedTrip.data.fare.listInbound.length - 1].firstSegment.departureAirport;
		} else {
			if (!_.isEmpty(bfmBookedTrip.data.fare.listOutbound)) {
				destinationAirportCity = bfmBookedTrip.data.fare.listOutbound[bfmBookedTrip.data.fare.listOutbound.length - 1].lastSegment.arrivalAirport;
			}
		}
		return destinationAirportCity;
	}

	IBE_allDatesSelected(bookingSteps: any): boolean {
		if (!bookingSteps || bookingSteps.length === 0) return false;
		for (let i = 0; i < bookingSteps.length; i++) {
			if (!bookingSteps[i].date) return false;
		}
		return true;
	}

	IBE_getDuration(booked_trip: any, bookingType?: string): string {

		const segmentsOutbound = booked_trip.data.fare.listOutbound;
		const segmentsInbound = booked_trip.data.fare.listInbound;
		let date;

		if (bookingType === TRIP_TYPE_ONE_WAY_DESCRIPTION) {
			return '';
		}

		if (!_.isEmpty(segmentsInbound)) {
			date = segmentsInbound[segmentsInbound.length - 1].lastSegment.arrivalDate;
		} else if (!_.isEmpty(segmentsOutbound)) {
			date = segmentsOutbound[segmentsOutbound.length - 1].lastSegment.arrivalDate;
		}

		const flightDuration = date ? this.dateService.INDRA_getRemainingTimeInSeconds(date) : 0;
		const flightDays = flightDuration ? (Math.floor(flightDuration / 60 / 60 / 24)).toFixed() : '';
		return flightDays;
	}

	IBE_getAllSegments(flights) {
		if (flights && flights.length != 0) {
			const flightSegments = [];
			for (const i in flights) {
				for (const x in flights[i].listSegment) {
					if (flightSegments.length == 0) {
						flightSegments.push(flights[i].listSegment[x].arrivalAirport);
					} else {
						if (!(flightSegments.includes(flights[i].listSegment[x].arrivalAirport))) {
							flightSegments.push(flights[i].listSegment[x].arrivalAirport);
						}

						if (!(flightSegments.includes(flights[i].listSegment[x].departureAirport))) {
							flightSegments.push(flights[i].listSegment[x].departureAirport);
						}
					}

				}
			}

			return flightSegments.join(', ');
		}

		return undefined;
	}

	// --------------- END STATIC DATALAYER PROCESSING -----------------//

	// --------------- START EVENT DATALAYER PROCESSING ----------------- //

	IBE_createGA_AncillariesProducts(addOnAncillaries: INDRAAddOnAncillary[], coupon?: string, fop?: PaymentMethod): EventDataLayerObject {
		const products = addOnAncillaries.map(addOnAncillary => {
			return {
				id: addOnAncillary.code,
				name: this.ibeAdminService.getTranslationValue(
					TRANSLATION_ANCILLARY_DETAILED + addOnAncillary.code,
					true),
				category: EVENT_CATEGORY_ANCILLARIES,
				variant: moment().format('DD.MM.YYYY'),
				brand: EVENT_PRODUCT_BRAND_TAP,
				quantity: addOnAncillary.quantity ? addOnAncillary.quantity.toString() : undefined,
				price: addOnAncillary.payWithMiles ? '0' : (addOnAncillary.price ? addOnAncillary.price.toString() : undefined),
				coupon: coupon,
				dimension1: addOnAncillary.payWithMiles && addOnAncillary.points ? addOnAncillary.points.toString() : undefined,
				dimension2: !_.isNil(fop) ? this.getDimension2(fop, (addOnAncillary.payWithMiles && addOnAncillary.points && addOnAncillary.points > 0 ? true: false)) : undefined
			} as EventDataLayerProductObject;
		});
		return { products: products } as EventDataLayerObject;
	}

	IBE_createGA_AncillaryProducts(addOnAncillary: INDRAAddOnAncillary, name?: string, coupon?: string): EventDataLayerObject {
		const product = {
			id: addOnAncillary.code,
			name: name ? name : this.ibeAdminService.getTranslationValue(
				TRANSLATION_ANCILLARY_DETAILED + addOnAncillary.code,
				true),
			category: EVENT_CATEGORY_ANCILLARIES,
			variant: moment().format('DD.MM.YYYY'),
			brand: EVENT_PRODUCT_BRAND_TAP,
			quantity: addOnAncillary.quantity ? addOnAncillary.quantity.toString() : undefined,
			price: addOnAncillary.price ? addOnAncillary.price.toString() : undefined,
			coupon: coupon
		} as EventDataLayerProductObject;

		return { products: [product] } as EventDataLayerObject;
	}


	IBE_createGA_ExternalAncillaryProducts(activeBooking, quantity?: number | string, coupon?: string, fop?: PaymentMethod): EventDataLayerObject {
		const products = [];
		const product = {
			id: undefined,
			name: undefined,
			category: EVENT_CATEGORY_ANCILLARIES,
			variant: moment().format('DD.MM.YYYY'),
			brand: undefined,
			quantity: quantity ? quantity : this.staticDataLayerObject.passengers_nr,
			price: undefined,
			coupon: coupon
		} as EventDataLayerProductObject;

		if (activeBooking.danish_fund) {
			const danishFund = _.cloneDeep(product);
			danishFund.id = activeBooking.danish_fund[0].bookableItemId;
			danishFund.name = activeBooking.danish_fund[0].description;
			danishFund.brand = EVENT_PRODUCT_BRAND_DANISH_FUND;
			danishFund.price = activeBooking.danish_fund[0].amount.toFixed(2);
			danishFund.dimension2 = !_.isNil(fop) ? this.getDimension2(fop, false) : undefined;
			products.push(danishFund);
		}

		if (activeBooking.carbon_offset) {
			const carbonOffset = _.cloneDeep(product);
			carbonOffset.id = activeBooking.carbon_offset[0].bookableItemId;
			carbonOffset.name = activeBooking.carbon_offset[0].description;
			carbonOffset.brand = EVENT_PRODUCT_BRAND_CARBON_OFFSET;
			carbonOffset.price = activeBooking.carbon_offset[0].amount.toFixed(2);
			carbonOffset.dimension2 = !_.isNil(fop) ? this.getDimension2(fop, false) : undefined;
			products.push(carbonOffset);
		}

		if (activeBooking.insurance) {
			const insurance = _.cloneDeep(product);
			insurance.id = activeBooking.insurance[0].bookableItemId;
			insurance.name = activeBooking.insurance[0].description;
			insurance.brand = EVENT_PRODUCT_BRAND_CHUBB_INSURANCE;
			insurance.price = activeBooking.insurance[0].amount.toFixed(2);
			insurance.dimension2 = !_.isNil(fop) ? this.getDimension2(fop, false) : undefined;
			products.push(insurance);
		}

		return { products: products } as EventDataLayerObject;
	}

	IBE_createGA_ExtrasProducts(extra, quantity?: number | string, coupon?: string, fop?: PaymentMethod): EventDataLayerObject {
		const products = [];
		const product = {
			id: undefined,
			name: undefined,
			category: EVENT_CATEGORY_EXTRAS,
			variant: moment().format('DD.MM.YYYY'),
			brand: undefined,
			// quantity: quantity ? quantity : this.staticDataLayerObject.passengers_nr,
			quantity: "1",
			price: undefined,
			coupon: coupon
		} as EventDataLayerProductObject;

		if (extra.rented_car && extra.rented_car.length !== 0) {
			const rented_car = _.cloneDeep(product);
			rented_car.id = extra.rented_car[0].payload.product.id;
			rented_car.name = extra.rented_car[0].payload.product.name;
			rented_car.price = extra.rented_car[0].payload.product.price.total.amount;
			rented_car.dimension2 = !_.isNil(fop) ? this.getDimension2(fop, false) : undefined;
			products.push(rented_car);
		}

		if (extra.hotel) {
			const hotel = _.cloneDeep(product);
			hotel.id = extra.hotel.idReservation ? extra.hotel.idReservation : extra.hotel.hotel.hotelId;
			hotel.name = extra.hotel.extraInfo ? extra.hotel.extraInfo.name : extra.hotel.hotel.hotelInfo.hotel_name;
			hotel.price = extra.hotel.amount ? extra.hotel.amount : extra.hotel.value;
			products.push(hotel);
		}

		if (extra.airport_transfer) {
			const vehicle = _.cloneDeep(product);
			vehicle.id = extra.airport_transfer.idReservation;
			vehicle.name = extra.airport_transfer.provider;
			vehicle.price = extra.airport_transfer.stopOverInfo.SO_AIRPORT_TRANSFER.amount;
			products.push(vehicle);
		}

		if (extra.sightseeing_tour) {
			const tour = _.cloneDeep(product);
			tour.id = extra.sightseeing_tour.idReservation;
			tour.name = extra.sightseeing_tour.description;
			tour.price = extra.sightseeing_tour.amount;
			products.push(tour);
		}

		if (extra.lisboa_card) {
			const lisboa = _.cloneDeep(product);
			lisboa.id = extra.lisboa_card.idReservation;
			lisboa.name = extra.lisboa_card.provider;
			lisboa.price = extra.lisboa_card.stopOverInfo.SO_LISBON_DISCOUNT.amount;
			products.push(lisboa);
		}
		

		return { products: products } as EventDataLayerObject;
	}

	IBE_createGA_FlightProducts(code: string, name: string, category?: string, selected_brand?: Brand, selected_price?: number | string, coupon?: string, miles?: number, fop?: PaymentMethod): EventDataLayerObject {
		const product = {
			id: code,
			name: name,
			category: category ? category : EVENT_CATEGORY_FLIGHTS,
			variant: moment().format('DD.MM.YYYY'),
			brand: EVENT_PRODUCT_BRAND_TAP,
			quantity: '1',
			price: selected_price ? selected_price : (selected_brand && selected_brand.price) ? selected_brand.price.toString() : undefined,
			coupon: this.promocoCodesAndVoucher.map(promo => promo.code && promo.status === DISCOUNT_STATUS_ACTIVE).join(', '),
			dimension1: !_.isNil(miles) && miles > 0 ? miles.toString() : undefined,
			dimension2: !_.isNil(fop) ? this.getDimension2(fop, false) : undefined
		} as EventDataLayerProductObject;
		return { products: [product] } as EventDataLayerObject;
	}

	IBE_GA_purchase(currencyCode: string, eventDataLayerObject: EventDataLayerObject, miles?: number, fop?: PaymentMethod) {
		this.INDRA_clearEcommerce();
		
		const products = this.INDRA_setPricesPerItem(eventDataLayerObject.products);

		// window.dataLayer.push({
		// 	event: EVENT_TRIGGER_PURCHASE,
		// 	ecommerce: {
		// 		currencyCode: currencyCode,
		// 		purchase: {
		// 			actionField: {
		// 				id: eventDataLayerObject.actionField.id,
		// 				revenue: eventDataLayerObject.actionField.revenue,
		// 				tax: eventDataLayerObject.actionField.tax,
		// 				coupon: eventDataLayerObject.actionField.coupon
		// 			},
		// 			products: products
		// 			/*
		// 			products: {
		// 				...eventDataLayerObject.products,
		// 				dimension1: !_.isNil(miles) ? miles : 0,
		// 				dimension2: !_.isNil(fop) ? this.getDimension2(fop, !_.isNil(miles)) : undefined
		// 			}
		// 			*/
		// 		}
		// 	}
		// });

		const dataLayerObject = {
			event: EVENT_TRIGGER_PURCHASE,
			ecommerce: {
				currencyCode: currencyCode,
				purchase: {
					actionField: {
						id: eventDataLayerObject.actionField.id,
						revenue: eventDataLayerObject.actionField.revenue,
						tax: eventDataLayerObject.actionField.tax,
						coupon: eventDataLayerObject.actionField.coupon
					},
					products: products
				}
			}
		};

		this.INDRA_GA_pushEcommerceObject(dataLayerObject);

		console.log(JSON.stringify(window.dataLayer));
	}
	
	IBE_send_GA_purchase(ga_purchase_event_object) {
		// window.dataLayer.push(ga_purchase_event_object);
		this.INDRA_GA_pushEcommerceObject(ga_purchase_event_object);
		console.log(JSON.stringify(window.dataLayer));
	}
	
	IBE_create_GA_purchaseEvent_object(currencyCode: string, eventDataLayerObject: EventDataLayerObject, miles?: number, fop?: PaymentMethod) {
		this.INDRA_clearEcommerce();
		
		const products = this.INDRA_setPricesPerItem(eventDataLayerObject.products);
		const gaPurchaseEventObj = {
			event: EVENT_TRIGGER_PURCHASE,
			ecommerce: {
				currencyCode: currencyCode,
				purchase: {
					actionField: {
						id: eventDataLayerObject.actionField.id,
						revenue: eventDataLayerObject.actionField.revenue,
						tax: eventDataLayerObject.actionField.tax,
						coupon: eventDataLayerObject.actionField.coupon
					},
					products: products
				}
			}
		};
		return gaPurchaseEventObj;
	}

	IBE_groupAncillaries(ancillaryList) {
		const extras = [];
		if (ancillaryList && ancillaryList.itemsAdded.length) {
			ancillaryList.itemsAdded.forEach(addedExtra => {
				const index = extras.findIndex(extra => extra.code === addedExtra.code);
				if (index === -1) {
					addedExtra.quantity = 1;
					extras.push(_.cloneDeep(addedExtra));
				} else {
					extras[index].price ? extras[index].price = +extras[index].price + +addedExtra.price : extras[index].price = 0;
					extras[index].points ? extras[index].points = +extras[index].points + +addedExtra.points : extras[index].points = 0;
					extras[index].quantity++;
				}
			});
		}

		return extras;
	}


	// --------------- END EVENT DATALAYER PROCESSING ----------------- //

	// -------------------------------- END IBE METHODS ---------------------------------- //

	private getStaticDataLayerObject(): Observable<StaticDataLayerObject> {
		this.resetActiveBookedTrip();

		const market$ = this.languageMarketService.market$;
		const language$ = this.languageMarketService.language$;
		const booking$ = this.getBookingObservableByRouter();

		return combineLatest(market$, language$, booking$)
			.pipe(
				filter(observables$ => {
					return this.checkObservablesPerRoute(...observables$);
				}),
				map(source => {
					const [mkt, lang, booked_trip] = source;

					this.staticDataLayerObject.market = mkt;
					this.staticDataLayerObject.language = lang;
					this.staticDataLayerObject.site = 'MYB';
					this.staticDataLayerObject.pageName = this.getPageName(this.currentUrl);
					this.staticDataLayerObject.pageUrl = this.currentUrl;

					// logged user
					if (this.storageService && this.storageService.get('userData')) {
						const userData = this.storageService.get('userData');
						this.staticDataLayerObject.loggedID = userData.airlineID;
						this.staticDataLayerObject.victoria_user = !_.isEmpty(userData.ffCarrier) && userData.ffCarrier === 'TP' && !_.isEmpty(userData.ffLevel) ? '1' : '0';
						this.staticDataLayerObject.tier_level = !_.isEmpty(userData.ffLevel) ? userData.ffLevel : '';
						this.staticDataLayerObject.miles_balance = userData.currentPoints;
						const consents = userData.consents;
						const forCode = [
							(consents.tappp ? '1' : '0'),
							(consents.tapsms ? '1' : '0'),
							(consents.tapemail ? '1' : '0'),
							(consents.loytc ? '1' : '0'),
							(consents.loysms ? '1' : '0'),
							(consents.loyemail ? '1' : '0'),
							(consents.tapprof ? '1' : '0'),
							(consents.tappush ? '1' : '0'),
							(consents.loypush ? '1' : '0')
						].join('');
						this.staticDataLayerObject.for_code = forCode;
					}

					// after retrieve pnr information
					if (_.isNil(booked_trip)) {
						this.cleanStaticDataLayer();
					} else {
						this.staticDataLayerObject.pnr_nr = this.bookingService.getCurrentPNR();
						this.staticDataLayerObject.flight_type = this.getTripTypeName(booked_trip.data.fare.tripType);

						const originAirportCity = this.getOriginAirportCity(booked_trip);
						this.staticDataLayerObject.flight_originairportcity = originAirportCity;

						const destinationAirportCity = this.getDestinationAirportCity(booked_trip);
						this.staticDataLayerObject.flight_destinationairportcity = destinationAirportCity;

						if (booked_trip.translate && booked_trip.translate.locations && booked_trip.translate.locations[originAirportCity]) {
							this.staticDataLayerObject.flight_origincityname = booked_trip.translate.locations[originAirportCity].city;
						}
						if (booked_trip.translate && booked_trip.translate.locations && booked_trip.translate.locations[destinationAirportCity]) {
							this.staticDataLayerObject.flight_destinationcityname = booked_trip.translate.locations[destinationAirportCity].city;
						}
						this.staticDataLayerObject.flight_segments = this.getSegments(booked_trip);
						this.staticDataLayerObject.flight_route = originAirportCity + '-' + destinationAirportCity;
						this.staticDataLayerObject.flight_departuredate = this.dateService.INDRA_formatDate(booked_trip.data.fare.listOutbound[0].firstSegment.departureDate.toString(), 'DD.MM.YYYY');
						this.staticDataLayerObject.flight_returndate = this.getReturnDate(booked_trip);

						// finish implementing
						this.staticDataLayerObject.flight_haul = this.getHauls(booked_trip);
						this.staticDataLayerObject.flight_brand = this.getBrands(booked_trip);
						this.staticDataLayerObject.flight_rbd = this.getRBDs(booked_trip);
						this.staticDataLayerObject.flight_cabin = this.getCabins(booked_trip);
						this.staticDataLayerObject.flight_number = this.getFlightNumbers(booked_trip);
						this.staticDataLayerObject.flight_stopover = this.getStopover(booked_trip);
						this.staticDataLayerObject.flight_timetothink = booked_trip.data.isTTT ? 'yes' : 'no';
						this.staticDataLayerObject.flight_bookingdate = this.dateService.INDRA_formatDate(booked_trip.data.creationTime.toString(), 'DD.MM.YYYY');
						this.staticDataLayerObject.travel_duration = this.getDuration(booked_trip);

						const listPax = booked_trip.data.infoPax.listPax;
						this.staticDataLayerObject.passengers_nr = listPax.length.toString();
						this.staticDataLayerObject.adults_nr = this.getPaxNrByType(listPax, PAX_TYPE_ADULT_CODE);
						this.staticDataLayerObject.infants_nr = this.getPaxNrByType(listPax, PAX_TYPE_INFANT_CODE);
						this.staticDataLayerObject.children_nr = this.getPaxNrByType(listPax, PAX_TYPE_CHILD_CODE);
						this.staticDataLayerObject.youngadult_nr = this.getPaxNrByType(listPax, PAX_TYPE_YOUNG_ADULT_CODE);
					}

					return this.staticDataLayerObject;
				})
			);
	}

	private cleanStaticDataLayer() {
		this.staticDataLayerObject.pnr_nr = undefined;
		this.staticDataLayerObject.flight_type = undefined;
		this.staticDataLayerObject.flight_originairportcity = undefined;
		this.staticDataLayerObject.flight_destinationairportcity = undefined;
		this.staticDataLayerObject.flight_origincityname = undefined;
		this.staticDataLayerObject.flight_destinationcityname = undefined;
		this.staticDataLayerObject.flight_segments = undefined;
		this.staticDataLayerObject.flight_route = undefined;
		this.staticDataLayerObject.flight_departuredate = undefined;
		this.staticDataLayerObject.flight_returndate = undefined;
		this.staticDataLayerObject.flight_haul = undefined;
		this.staticDataLayerObject.flight_brand = undefined;
		this.staticDataLayerObject.flight_rbd = undefined;
		this.staticDataLayerObject.flight_cabin = undefined;
		this.staticDataLayerObject.flight_number = undefined;
		this.staticDataLayerObject.flight_stopover = undefined;
		this.staticDataLayerObject.flight_timetothink = undefined;
		this.staticDataLayerObject.flight_bookingdate = undefined;
		this.staticDataLayerObject.travel_duration = undefined;
		this.staticDataLayerObject.passengers_nr = undefined;
		this.staticDataLayerObject.adults_nr = undefined;
		this.staticDataLayerObject.infants_nr = undefined;
		this.staticDataLayerObject.children_nr = undefined;
		this.staticDataLayerObject.youngadult_nr = undefined;

		this.staticDataLayerObject = _.pickBy(this.staticDataLayerObject, _.identity);
	}

	private isBookingPossibleInStorageByRouter(): boolean {
		const paths = this.currentUrl.split('/').filter(v => !_.isEmpty(v));
		const first = _.head(paths);
		const tail = _.tail(paths);
		return Object.is('my-bookings', first) && tail.length > 0;
	}

	private getBookingObservableByRouter(): Observable<INDRABookedTripDetails> {
		if (this.isBookingPossibleInStorageByRouter()) {
			return this.dataStorage.INDRA_getObservableCurrentBooking();
		}
		return of(null);
	}

	private checkObservablesPerRoute(...values: any[]): boolean {
		const [mkt, lang, booking] = values;
		const requiredValues = !_.isNil(mkt) && !_.isNil(lang);
		if (this.isBookingPossibleInStorageByRouter()) {
			return requiredValues && !_.isNil(booking);
		}
		return requiredValues;
	}

	private resetActiveBookedTrip() {
		const paths = this.currentUrl.split('/').filter(v => !_.isEmpty(v));
		const first = _.head(paths);
		const tail = _.tail(paths);
		const previousUrlLast = _.last(this.previousUrl.split('/'));
		const firstCondition = Object.is(first, 'my-bookings') && tail.length === 0;
		const secondCondition = Object.is(first, 'my-bookings')
			&& Object.is('details', tail[0])
			&& (!_.isNil(tail[1]) && tail[1].match('^[a-zA-Z0-9]{6}$'))
			&& (!_.isNil(tail[2]) && tail[2].match('^[a-zA-Z]+([ a-zA-Z]+)?$'))
			&& (!_.isNil(previousUrlLast) && !previousUrlLast.match('^[a-zA-Z]+([ a-zA-Z]+)?\\(sidebar:[a-zA-Z0-9-&_;?\\)=]+$'));

		if (firstCondition || secondCondition) {
			this.dataStorage.INDRA_setActiveBookedTrip(null);
		}
	}

	private getPageName(url: string) {
		const urlParts = url.split('/');
		let pageName = urlParts[1];
		if (urlParts[2]) pageName += '/' + urlParts[2];
		if (urlParts[4] && urlParts[4].includes('sidebar'))
			pageName +=
				'/sidebar/' +
				urlParts[4].substring(
					urlParts[4].indexOf(':') + 1,
					urlParts[4].indexOf(';')
				);
		return pageName;
	}

	private getTripTypeName(tripType: string) {
		switch (tripType) {
			case TRIP_TYPE_ONE_WAY_CODE:
				return TRIP_TYPE_ONE_WAY_DESCRIPTION;
			case TRIP_TYPE_ROUND_TRIP_CODE:
				return TRIP_TYPE_ROUND_TRIP_DESCRIPTION;
			case TRIP_TYPE_MULTICITY_CODE:
				return TRIP_TYPE_MULTICITY_DESCRIPTION;
			case TRIP_TYPE_HAS_STOP_OVER_CODE:
				return TRIP_TYPE_HAS_STOP_OVER_DESCRIPTION;
			default:
				return '';
		}
	}

	private getOriginAirportCity(booked_trip: any): string {
		let originAirportCity = '';
		if (!_.isEmpty(booked_trip.data.fare.listOutbound)) {
			originAirportCity = booked_trip.data.fare.listOutbound[0].firstSegment.departureAirport;
		}
		return originAirportCity;
	}

	private getDestinationAirportCity(booked_trip: any): string {
		let destinationAirportCity = '';
		if (!_.isEmpty(booked_trip.data.fare.listInbound)) {
			destinationAirportCity = booked_trip.data.fare.listInbound[0].firstSegment.departureAirport;
		} else {
			if (!_.isEmpty(booked_trip.data.fare.listOutbound)) {
				destinationAirportCity = booked_trip.data.fare.listOutbound[booked_trip.data.fare.listOutbound.length - 1].firstSegment.departureAirport;
			}
		}
		return destinationAirportCity;
	}

	private getSegments(booked_trip: any): string {
		let segments = '';
		let lastSegmentDestination = '';
		if (booked_trip.data.fare.listOutbound) {
			booked_trip.data.fare.listOutbound.forEach(outbound => {
				outbound.listSegment.forEach(segment => {
					if (lastSegmentDestination === '') {
						lastSegmentDestination = segment.departureAirport;
						segments = segments.concat(segment.departureAirport);
						if (lastSegmentDestination !== segment.arrivalAirport) {
							lastSegmentDestination = segment.arrivalAirport;
							segments = segments.concat(', ' + segment.arrivalAirport);
						}
					} else {
						if (lastSegmentDestination !== segment.departureAirport) {
							lastSegmentDestination = segment.departureAirport;
							segments = segments.concat(', ' + segment.departureAirport);
						}
						if (lastSegmentDestination !== segment.arrivalAirport) {
							lastSegmentDestination = segment.arrivalAirport;
							segments = segments.concat(', ' + segment.arrivalAirport);
						}
					}
				});
			});
		}
		if (booked_trip.data.fare.listInbound) {
			booked_trip.data.fare.listInbound.forEach(inbound => {
				inbound.listSegment.forEach(segment => {
					if (lastSegmentDestination !== segment.departureAirport) {
						lastSegmentDestination = segment.departureAirport;
						segments = segments.concat(', ' + segment.departureAirport);
					}
					if (lastSegmentDestination !== segment.arrivalAirport) {
						lastSegmentDestination = segment.arrivalAirport;
						segments = segments.concat(', ' + segment.arrivalAirport);
					}
				});
			});
		}
		return segments;
	}

	private getReturnDate(booked_trip: any): string {
		let returnDate = '';
		if (!_.isEmpty(booked_trip.data.fare.listInbound)) {
			returnDate = this.dateService.INDRA_formatDate(
				booked_trip.data.fare.listInbound[booked_trip.data.fare.listInbound.length - 1].firstSegment.departureDate.toString(),
				'DD.MM.YYYY'
			);
		} else {
			if (!_.isEmpty(booked_trip.data.fare.listOutbound)) {
				returnDate = this.dateService.INDRA_formatDate(
					booked_trip.data.fare.listOutbound[booked_trip.data.fare.listOutbound.length - 1].firstSegment.departureDate.toString(),
					'DD.MM.YYYY'
				);
			}
		}
		return returnDate;
	}

	private getHauls(booked_trip: any): string {
		let hauls = '';
		if (booked_trip.data.fare.listOutbound) {
			booked_trip.data.fare.listOutbound.forEach(outbound => {
				outbound.listSegment.forEach(segment => {
					const haulType = this.getHaulType(segment.haul);
					if (hauls === '') {
						hauls = hauls.concat(haulType);
					} else {
						hauls = hauls.concat(', ' + haulType);
					}
				}); 
			});
		}
		if (booked_trip.data.fare.listInbound) {
			booked_trip.data.fare.listInbound.forEach(inbound => {
				inbound.listSegment.forEach(segment => {
					const haulType = this.getHaulType(segment.haul);
					hauls = hauls.concat(', ' + haulType);
				});
			});
		}
		return hauls;
	}

	private getHaulType(haul: any): string {
		switch (haul) {
			case 'HAUL_SHORT':
				return 'short haul';
			case 'HAUL_MEDIUM':
				return 'medium haul';
			case 'HAUL_LONG':
				return 'long haul';
			case 'HAUL_DOMESTIC':
				return 'domestic haul';
		}
	}

	private getBrands(booked_trip: any): string {
		let brands = '';
		if (booked_trip.data.fare.listOutbound) {
			booked_trip.data.fare.listOutbound.forEach(outbound => {
				const fareFamily = this.bookingService.INDRA_getBrandCode(outbound.fareFamily);
				outbound.listSegment.forEach(segment => {
					if (brands === '') {
						brands = brands.concat(fareFamily);
					} else {
						brands = brands.concat(', ' + fareFamily);
					}
				});
			});
		}
		if (booked_trip.data.fare.listInbound) {
			booked_trip.data.fare.listInbound.forEach(inbound => {
				const fareFamily = this.bookingService.INDRA_getBrandCode(inbound.fareFamily);
				inbound.listSegment.forEach(segment => {
					brands = brands.concat(', ' + fareFamily);
				});
			});
		}
		return brands;
	}

	private getRBDs(booked_trip: any): string {
		let rbds = '';
		if (booked_trip.data.fare.listOutbound) {
			booked_trip.data.fare.listOutbound.forEach(outbound => {
				outbound.listSegment.forEach(segment => {
					if (rbds === '') {
						rbds = rbds.concat(segment.rbd);
					} else {
						rbds = rbds.concat(', ' + segment.rbd);
					}
				});
			});
		}
		if (booked_trip.data.fare.listInbound) {
			booked_trip.data.fare.listInbound.forEach(inbound => {
				inbound.listSegment.forEach(segment => {
					rbds = rbds.concat(', ' + segment.rbd);
				});
			});
		}
		return rbds;
	}

	private getCabins(booked_trip: any): string {
		let cabins = '';
		if (booked_trip.data.fare.listOutbound) {
			booked_trip.data.fare.listOutbound.forEach(outbound => {
				outbound.listSegment.forEach(segment => {
					if (cabins === '') {
						cabins = cabins.concat(segment.cabin);
					} else {
						cabins = cabins.concat(', ' + segment.cabin);
					}
				});
			});
		}
		if (booked_trip.data.fare.listInbound) {
			booked_trip.data.fare.listInbound.forEach(inbound => {
				inbound.listSegment.forEach(segment => {
					cabins = cabins.concat(', ' + segment.cabin);
				});
			});
		}
		return cabins;
	}

	private getFlightNumbers(booked_trip: any): string {
		let flightNumbers = '';
		if (booked_trip.data.fare.listOutbound) {
			booked_trip.data.fare.listOutbound.forEach(outbound => {
				outbound.listSegment.forEach(segment => {
					if (flightNumbers === '') {
						flightNumbers = flightNumbers.concat(segment.carrier + ' ' + segment.flightNumber);
					} else {
						flightNumbers = flightNumbers.concat(', ' + segment.carrier + ' ' + segment.flightNumber);
					}
				});
			});
		}
		if (booked_trip.data.fare.listInbound) {
			booked_trip.data.fare.listInbound.forEach(inbound => {
				inbound.listSegment.forEach(segment => {
					flightNumbers = flightNumbers.concat(', ' + segment.carrier + ' ' + segment.flightNumber);
				});
			});
		}
		return flightNumbers;
	}

	private getStopover(booked_trip: any): string {
		let stopover = 'no';
		if (booked_trip.data.fare.listOutbound) {
			booked_trip.data.fare.listOutbound.forEach(outbound => {
				outbound.listSegment.forEach(segment => {
					if (stopover === 'no') {
						if (segment.stopover) {
							stopover = 'yes';
						}
					}
				});
			});
		}
		return stopover;
	}

	private getDuration(booked_trip: any): string {

		const segmentsOutbound = booked_trip.data.fare.listOutbound;
		const segmentsInbound = booked_trip.data.fare.listInbound;
		let flightDuration = 0;

		if (booked_trip.data.fare.tripType === 'O') {
			return '';
		}

		if (!_.isEmpty(segmentsOutbound)) {
			segmentsOutbound.forEach(segment => {
				flightDuration += (segment.duration + (segment.stopTime > 0 && !segment.stopover ? segment.stopTime : 0));
			});
		}
		if (!_.isEmpty(segmentsInbound)) {
			segmentsInbound.forEach(segment => {
				flightDuration += (segment.duration + (segment.stopTime > 0 && !segment.stopover ? segment.stopTime : 0));
			});
		}
		const flightDurationDays = flightDuration / 24;
		return flightDurationDays.toFixed();
	}

	private getPaxNrByType(listPax: any, paxType: string): string {
		return listPax.filter(function (pax) {
			return pax.typePax === paxType;
		}).length;
	}

	IBE_getAllBrands(flights) {
		if (flights && flights.length != 0) {
			const brandsArr = [];
			for (const i in flights) {
				brandsArr.push(flights[i].options.brand.name);
			}

			return brandsArr.join(', ');
		}

		return undefined;
	}

	INDRA_createGA_ActionField(list?: string, step?: string, id?: string, revenue?: string, tax?: string, coupon?: string): EventDataLayerActionFieldObject {
		return {
			list: list ? list : undefined,
			step: step ? step : undefined,
			id: id ? id : undefined,
			revenue: revenue ? revenue : undefined,
			tax: tax ? tax : undefined,
			coupon: coupon ? coupon : undefined,
		} as EventDataLayerActionFieldObject;
	}

	INDRA_createGA_AncillariesProducts(addOnAncillaries: INDRAAddOnAncillary[], form_of_payment?: PaymentMethod): EventDataLayerObject {
		const products = addOnAncillaries.map(addOnAncillary => {
			return {
				id: addOnAncillary.code,
				name: this.ibeAdminService.getTranslationValue(
					TRANSLATION_ANCILLARY_DETAILED + addOnAncillary.code,
					true),
				category: EVENT_CATEGORY_ANCILLARIES,
				variant: moment().format('DD.MM.YYYY'),
				brand: 'TAP',
				quantity: addOnAncillary.quantity ? addOnAncillary.quantity.toString() : undefined,
				price: addOnAncillary.price ? addOnAncillary.price.toString() : undefined,
				dimension2: !_.isNil(form_of_payment) ? this.getDimension2(form_of_payment) : undefined
			} as EventDataLayerProductObject;
		});
		return { products: products } as EventDataLayerObject;
	}

	INDRA_createGA_AncillaryProducts(addOnAncillary: INDRAAddOnAncillary, name?: string): EventDataLayerObject {
		const product = {
			id: addOnAncillary.code,
			name: name ? name : this.ibeAdminService.getTranslationValue(
				TRANSLATION_ANCILLARY_DETAILED + addOnAncillary.code,
				true),
			category: EVENT_CATEGORY_ANCILLARIES,
			variant: moment().format('DD.MM.YYYY'),
			brand: 'TAP',
			quantity: addOnAncillary.quantity ? addOnAncillary.quantity.toString() : undefined,
			price: addOnAncillary.price ? addOnAncillary.price.toString() : undefined
		} as EventDataLayerProductObject;

		return { products: [product] } as EventDataLayerObject;
	}

	INDRA_createGA_FlightProducts(code: string, name: string, category: string, selected_brand?: Brand, miles?: string, form_of_payment?: PaymentMethod): EventDataLayerObject {
		const product = {
			id: code,
			name: name,
			category: category,
			variant: moment().format('DD.MM.YYYY'),
			brand: 'TAP',
			quantity: '1',
			price: (selected_brand && selected_brand.price) ? selected_brand.price.toString() : undefined,
			dimension1: !_.isNil(miles) ? miles : 0,
			dimension2: !_.isNil(form_of_payment) ? this.getDimension2(form_of_payment, !_.isNil(miles)) : undefined
		} as EventDataLayerProductObject;
		return { products: [product] } as EventDataLayerObject;
	}

	INDRA_GA_productClick(eventDataLayerObject: EventDataLayerObject, currencyCode?: string) {
		this.INDRA_clearEcommerce();

		// window.dataLayer.push({
		// 	event: EVENT_TRIGGER_DETAIL,
		// 	ecommerce: {
		// 		currencyCode: currencyCode ? currencyCode : undefined,
		// 		detail: {
		// 			actionField: {
		// 				list: eventDataLayerObject.actionField.list
		// 			},
		// 			products: eventDataLayerObject.products
		// 		}
		// 	}
		// });

		const dataLayerObject = {
			event: EVENT_TRIGGER_DETAIL,
			ecommerce: {
				currencyCode: currencyCode ? currencyCode : undefined,
				detail: {
					actionField: {
						list: eventDataLayerObject.actionField.list
					},
					products: eventDataLayerObject.products
				}
			}
		};

		this.INDRA_GA_pushEcommerceObject(dataLayerObject);
	}

	INDRA_GA_addToCart(currencyCode: string, eventDataLayerObject: EventDataLayerObject) {
		this.INDRA_clearEcommerce();
		const products = this.INDRA_setPricesPerItem(eventDataLayerObject.products);

		// window.dataLayer.push({
		// 	event: EVENT_TRIGGER_ADD,
		// 	ecommerce: {
		// 		currencyCode: currencyCode,
		// 		add: {
		// 			actionField: {
		// 				list: eventDataLayerObject.actionField.list
		// 			},
		// 			products: products
		// 		}
		// 	}
		// });

		const dataLayerObject = {
			event: EVENT_TRIGGER_ADD,
			ecommerce: {
				currencyCode: currencyCode,
				add: {
					actionField: {
						list: eventDataLayerObject.actionField.list
					},
					products: products
				}
			}
		};

		this.INDRA_GA_pushEcommerceObject(dataLayerObject);

		// console.log(JSON.stringify(window.dataLayer));
	}

	INDRA_GA_removeFromCart(currencyCode: string, eventDataLayerObject: EventDataLayerObject) {
		this.INDRA_clearEcommerce();

		// window.dataLayer.push({
		// 	event: EVENT_TRIGGER_REMOVE,
		// 	ecommerce: {
		// 		currencyCode: currencyCode,
		// 		remove: {
		// 			actionField: {},
		// 			products: eventDataLayerObject.products
		// 		}
		// 	}
		// });

		const dataLayerObject = {
			event: EVENT_TRIGGER_REMOVE,
			ecommerce: {
				currencyCode: currencyCode,
				remove: {
					actionField: {},
					products: eventDataLayerObject.products
				}
			}
		};

		this.INDRA_GA_pushEcommerceObject(dataLayerObject);
	}

	INDRA_GA_checkout(currencyCode: string, eventDataLayerObject: EventDataLayerObject) {
		this.INDRA_clearEcommerce();

		const products = this.INDRA_setPricesPerItem(eventDataLayerObject.products);

		// window.dataLayer.push({
		// 	event: EVENT_TRIGGER_CHECKOUT,
		// 	ecommerce: {
		// 		currencyCode: currencyCode,
		// 		checkout: {
		// 			actionField: {
		// 				step: eventDataLayerObject.actionField.step
		// 			},
		// 			products: products
		// 		}
		// 	}
		// });

		const dataLayerObject = {
			event: EVENT_TRIGGER_CHECKOUT,
			ecommerce: {
				currencyCode: currencyCode,
				checkout: {
					actionField: {
						step: eventDataLayerObject.actionField.step
					},
					products: products
				}
			}
		};

		this.INDRA_GA_pushEcommerceObject(dataLayerObject);

		console.log(JSON.stringify(window.dataLayer));
	}

	INDRA_GA_purchase(currencyCode: string, eventDataLayerObject: EventDataLayerObject) {
		this.INDRA_clearEcommerce();

		// window.dataLayer.push({
		// 	event: EVENT_TRIGGER_PURCHASE,
		// 	ecommerce: {
		// 		currencyCode: currencyCode,
		// 		purchase: {
		// 			actionField: {
		// 				id: eventDataLayerObject.actionField.id,
		// 				revenue: eventDataLayerObject.actionField.revenue,
		// 				// tax: eventDataLayerObject.actionField.tax,
		// 				coupon: eventDataLayerObject.actionField.coupon
		// 			},
		// 			products: eventDataLayerObject.products
		// 		}
		// 	}
		// });

		const dataLayerObject = {
			event: EVENT_TRIGGER_PURCHASE,
			ecommerce: {
				currencyCode: currencyCode,
				purchase: {
					actionField: {
						id: eventDataLayerObject.actionField.id,
						revenue: eventDataLayerObject.actionField.revenue,
						// tax: eventDataLayerObject.actionField.tax,
						coupon: eventDataLayerObject.actionField.coupon
					},
					products: eventDataLayerObject.products
				}
			}
		};

		this.INDRA_GA_pushEcommerceObject(dataLayerObject);
	}

	INDRA_getGA_DefaultAncillaryCode(ancillaryType: string, flightId: string, flightCardId: string) {
		const arrayIndexZero = 0;
		const defaultPaxId = 1;
		const ancillaryBundleByType = this.bookingService.INDRA_getAddOnAncillaries(ancillaryType, flightId, flightCardId);
		let defaultAncillaryCode = ancillaryBundleByType[arrayIndexZero][defaultPaxId][arrayIndexZero].code;
		if (Object.is(ancillaryType, EXTRAS_ANCILLARY_MEAL) && Object.is(defaultAncillaryCode, SSR_CODE_MEAL_CHILD)) {
			defaultAncillaryCode = ancillaryBundleByType[arrayIndexZero][defaultPaxId][arrayIndexZero + 1].code;
		}
		return defaultAncillaryCode;
	}

	INDRA_getGA_CurrencyCode(addOnAncillaries: INDRAAddOnAncillary[]): string {
		let currencyCode = null;
		addOnAncillaries.forEach(addOnAncillary => {
			if (currencyCode === null) {
				if (addOnAncillary.currency && addOnAncillary.currency !== '') {
					currencyCode = addOnAncillary.currency;
				}
			}
		});
		return currencyCode;
	}

	private getDimension2(formOfPayment: PaymentMethod, isUpgradeWithMiles?: boolean): EventPaymentCashEnumDimension2 {
		if (isUpgradeWithMiles) {
			return EventPaymentCashEnumDimension2.Miles;
		}
		switch (formOfPayment) {
			case 'bank_transfer':
				return EventPaymentCashEnumDimension2.BankTransfer;
			case 'credit_card':
				return EventPaymentCashEnumDimension2.CreditCard;
			case 'debit_card':
				return EventPaymentCashEnumDimension2.DebitCard;
			case 'mb_way':
				return EventPaymentCashEnumDimension2.MBWAY;
			case 'multibanco':
				return EventPaymentCashEnumDimension2.Multibanco;
			case 'alipay':
				return EventPaymentCashEnumDimension2.Alipay;
			case 'paypal':
				return EventPaymentCashEnumDimension2.Paypal;
			case 'klarna_pay_now':
				return EventPaymentCashEnumDimension2.KlarnaPayNow;
			case 'klarna_pay_later':
				return EventPaymentCashEnumDimension2.KlarnaPayLater;
			case 'poli':
				return EventPaymentCashEnumDimension2.POLi;
			case 'ideal':
				return EventPaymentCashEnumDimension2.IDeal;
			case 'multicaixa':
				return EventPaymentCashEnumDimension2.Multicaixa;
			case 'pickup':
				return EventPaymentCashEnumDimension2.Pickup;
			case 'caixa_loterias':
				return EventPaymentCashEnumDimension2.CaixaLoterias;
			case 'bank_transfer_br':
				return EventPaymentCashEnumDimension2.BankTransferBr;
			default:
				return undefined;
		}
	}

	INDRA_createGA_ExternalAncillarySingleProduct(brand, activeBooking?, quantity?: number | string, coupon?: string): EventDataLayerObject {
		const externalAncillary = {
			id: undefined,
			name: undefined,
			category: EVENT_CATEGORY_ANCILLARIES,
			variant: moment().format('DD.MM.YYYY'),
			brand: brand,
			quantity: quantity ? quantity : this.staticDataLayerObject.passengers_nr,
			price: undefined,
			coupon: coupon
		} as EventDataLayerProductObject;

		const product = _.cloneDeep(externalAncillary);

		if (activeBooking) {
			product.id = activeBooking.bookableItemId;
			product.name = activeBooking.description;
			product.price = activeBooking.amount;
		}

		return { products: [product] } as EventDataLayerObject;
	}

  INDRA_setGAPageView() {
    /** 
     * Only fire pageview on NavigationEnd
     * Meaning, it will fire on new page only once
     */
    const pageView = {
      event: 'pageview',
      page: {
        path: this.currentUrl,
        name: this.getPageName(this.currentUrl),
        title: this.titleService.getTitle()
      }
    };
    // Note: This is important to fire pageview on first load
    // Since this.router is already done. So this is a workaround
	let triggerPageView = false;
    if(this.onLoadPageView) {
		this.onLoadPageView = false;
		triggerPageView = true;
    } else if (this.pageViewWithBooking) {
		this.pageViewWithBooking = false;
		triggerPageView = true;
	} else if (this.lastPageUrl != this.currentUrl) {
		triggerPageView = true;
	}

	if (triggerPageView) {
		window.dataLayer.push(pageView);
		this.lastPageUrl = this.currentUrl;
		this.INDRA_GA_triggerEcommerceEvent();
	}
  }

	INDRA_setPricesPerItem(eventDataLayerProducts) {
		eventDataLayerProducts.forEach((product, index) => {			
			if (+product.quantity > 1) {				
				if (product.price && !product['dimension1']) {
					const pricePerSimilarProduct = (+product.price / +product.quantity).toString();

					eventDataLayerProducts[index] = {...product, price: pricePerSimilarProduct};
				} else if (!+product.price && product['dimension1']) {
					const milesPerSimilarProduct = (+product.dimension1 / +product.quantity).toString();

					eventDataLayerProducts[index] = {...product, dimension1: milesPerSimilarProduct};
				}
			}
		});		

		return eventDataLayerProducts;
	}

	INDRA_setDataLayerAndPageView(routerState, setGAPageView) {		
		this.setRoutes(routerState);
		this.INDRA_setGAStaticDataLayer(setGAPageView);
	}

	// Call this before an ecommerce event (eecibe) to clear previous datalayer object
	INDRA_clearEcommerce() {
		window.dataLayer.push({ ecommerce: null });
	}

	INDRA_GA_pushEcommerceObject(dataLayerObject) {
		if (!this.dataLayerObjects) {
			this.dataLayerObjects = [];
		}

		this.dataLayerObjects.push(dataLayerObject);

		this.observatory.setData(KEY_GA_EECIBE, this.dataLayerObjects);
	}

	INDRA_GA_triggerEcommerceEvent() {
		if (this.observatory.checkData(KEY_GA_EECIBE)) {
			this.observatory.getData(KEY_GA_EECIBE).subscribe((response) => {
				console.log(response);
				if (response && isArray(response)) {
					response.forEach((dataLayerObject) => {
						window.dataLayer.push(dataLayerObject);
					});
				}
			});

			this.observatory.removeData(KEY_GA_EECIBE);
			this.dataLayerObjects = null;
		}
	}

	IBE_GA_EmptyResult(route, tripType?) {
		if (!tripType) {
			tripType = this.bookingSteps.length == 1 ? TRIP_TYPE_ONE_WAY_CODE : TRIP_TYPE_ROUND_TRIP_CODE;
		}
		
		let emptyEventCategory = '';
		const flightType = this.IBE_getTripTypeName(this.bookingSteps, tripType);
		const departureDate = moment(this.bookingSteps[0].date).format('DD.MM.YYYY');
		emptyEventCategory = `${route}|` + `${flightType}|` + `${departureDate}|`;
		
		window.dataLayer.push({
			event: 'EmptySearchResult',
			category: emptyEventCategory
		});
	}

	IBE_GA_PassengerError(error) {
		window.dataLayer.push({
			event: 'Errors',
			category: 'Step: passenger',
			action: error
		});
	}

	IBE_GA_PaymentError(error) {
		window.dataLayer.push({
			event: 'Errors',
			category: 'Step: payment',
			action: error
		});
	}

	IBE_GA_setAdditionalStepsPaymentPage(value) {
		// Pending: Temporary path, name and title, waiting for UX changes
		let pagePath;
		let pageName;
		let pageTitle;
		
		switch (value) {
			case 'login':
				pagePath = '/booking/login';
				pageName = '/booking/login';
				pageTitle = 'Booking - Login';
				break;
			case 'passenger':
				pagePath = '/booking/passenger';
				pageName = '/booking/passenger';
				pageTitle = 'Booking - Passenger';
				break;
			case 'contacts':
				pagePath = '/booking/contact';
				pageName = '/booking/contact';
				pageTitle = 'Booking - Contact';
				break;
			case 'payment':
				pagePath = '/booking/payment';
				pageName = '/booking/payment';
				pageTitle = 'Booking - Payment';
				break;
		}

		window.dataLayer.push({
			event: 'pageview',
			page: {
				path: pagePath,
				name: pageName,
				title: pageTitle
			}
		});
	}

}
